import axios from 'axios';
import Cookies from 'js-cookie';
import { isNil, isEmpty } from 'lodash';
import { DESIGN_API_URL, BAY_API_URL } from '../lib/environment.js';
import { axios as bayAxios } from '../lib/axios.js';

import { newAbortSignalFunc } from './modules/api.js';
import { escapeRegExp } from '@bay-ui/BayPolotnoEditor/utils';

const templates = () => {
	let templatesCancelToken = axios.CancelToken.source();
	let timeoutAmount = 180000; //Aborts request after 30 seconds

	// `source.type` is required on create (polotnoJson for design, html for
	// email) but immutable afterwards, so it must be stripped from update
	// payloads. Pass keepSourceType=true for the create path only.
	const sanitizeTemplateMutationPayload = (payload, { keepSourceType = false } = {}) => {
		if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
			return payload;
		}

		const sanitizedPayload = { ...payload };
		delete sanitizedPayload.user;

		// Root `jsonContext` ({ user, listing, altos }) is previewer-only
		// substitution context injected into template state by the preview flow.
		// It is never a persisted server field and must not ride along on
		// create/update payloads. (The persisted `etc.jsonContext` string used by
		// the email load path lives under `etc` and is intentionally untouched.)
		delete sanitizedPayload.jsonContext;

		if (
			sanitizedPayload.source &&
			typeof sanitizedPayload.source === 'object' &&
			!Array.isArray(sanitizedPayload.source)
		) {
			sanitizedPayload.source = { ...sanitizedPayload.source };
			// Server-owned metadata — computed by the design service, never
			// client-set, and rejected by its field allowlist.
			delete sanitizedPayload.source.containsVideo;
			delete sanitizedPayload.source.dtUpd;
			// source.type is required on create but immutable afterwards.
			if (!keepSourceType) delete sanitizedPayload.source.type;
		}

		Object.keys(sanitizedPayload).forEach((key) => {
			if (
				key.startsWith('user.') ||
				key === 'jsonContext' ||
				key.startsWith('jsonContext.') ||
				key === 'source.containsVideo' ||
				key === 'source.dtUpd' ||
				(!keepSourceType && key === 'source.type')
			) {
				delete sanitizedPayload[key];
			}
		});

		return sanitizedPayload;
	};

	const toDotNotationPatchPayload = (payload) => {
		if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
			return payload;
		}

		const patchPayload = { ...payload };

		if (
			patchPayload.source &&
			typeof patchPayload.source === 'object' &&
			!Array.isArray(patchPayload.source)
		) {
			Object.entries(patchPayload.source).forEach(([sourceKey, sourceValue]) => {
				if (sourceValue !== undefined) {
					patchPayload[`source.${sourceKey}`] = sourceValue;
				}
			});

			delete patchPayload.source;
		}

		return patchPayload;
	};

	const unwrapResult = (payload) => {
		if (!payload || typeof payload !== 'object') {
			return payload;
		}

		return Object.prototype.hasOwnProperty.call(payload, 'result')
			? payload.result
			: payload;
	};

	const hasNameSearchQuery = (query) => {
		if (!query || typeof query !== 'object') {
			return false;
		}

		if (
			query.name &&
			typeof query.name === 'object' &&
			!Array.isArray(query.name) &&
			!isNil(query.name.$regex)
		) {
			return true;
		}

		if (
			Array.isArray(query.$or) &&
			query.$or.some((clause) => hasNameSearchQuery(clause))
		) {
			return true;
		}

		if (
			Array.isArray(query.$and) &&
			query.$and.some((clause) => hasNameSearchQuery(clause))
		) {
			return true;
		}

		return false;
	};

	const isHashSearchValue = (value) => /^[a-f0-9]{24}$/i.test(value);

	return {
		cancel: () => {
			templatesCancelToken.cancel('Operation canceled by the user');
			templatesCancelToken = axios.CancelToken.source();
		},
		create: (data) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				const payload = sanitizeTemplateMutationPayload(data, {
					keepSourceType: true,
				});

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/templates`,
					method: 'POST',
					cache: 'no-cache',
					data: payload,
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(unwrapResult(resp.data));
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		read: (readArgs) => {
			return new Promise((resolve, reject) => {
				const {
					templateId = null,
					queryParams,
					limit = 24,
					offset = 0,
					sort = { dtUpd: 'desc' },
					havingAnyOfOwners = null,
					havingAllOfTags$Category = null,
					havingAllOfTags$General = null,
					havingAnyOfTags$General = null,
				} = readArgs;

				const controller = new AbortController();
				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				// isUncategorized
				const params = new URLSearchParams({
					q: JSON.stringify({ ...queryParams }),
					limit,
					offset,
					sort: JSON.stringify(
						hasNameSearchQuery(queryParams) ? { name: 'asc' } : sort,
					),
				});

				// FIXED: Enable havingAnyOfOwners parameter for cleaner owner filtering
				if (havingAnyOfOwners && havingAnyOfOwners.length > 0) {
					params.append('havingAnyOfOwners', JSON.stringify(havingAnyOfOwners));
				}

				if (havingAllOfTags$Category && havingAllOfTags$Category?.length > 0) {
					params.append(
						'havingAllOfTags$Category',
						JSON.stringify(havingAllOfTags$Category),
					);
				}

				if (havingAllOfTags$General && havingAllOfTags$General?.length > 0) {
					params.append(
						'havingAllOfTags$General',
						JSON.stringify(havingAllOfTags$General),
					);
				}

				// FIXED: Enable havingAnyOfTags$General parameter to exclude templates by tag (e.g., email templates)
				if (havingAnyOfTags$General && havingAnyOfTags$General.length > 0) {
					params.append(
						'havingAnyOfTags$General',
						JSON.stringify(havingAnyOfTags$General),
					);
				}

				if (isNil(templateId) || isEmpty(templateId)) {
					axios({
						url: `${DESIGN_API_URL}/templates/?${params.toString()}`,
						method: 'GET',
						cache: 'no-cache',
						signal: newAbortSignalFunc(timeoutAmount),
						cancelToken: templatesCancelToken.token,
					})
						.then((resp) => {
							clearTimeout(timeout);

							if (resp.data.status === 0) {
								resolve(resp.data);
							} else {
								reject(resp.data);
							}
						})
						.catch((err) => {
							clearTimeout(timeout);

							reject(err);
						});
				} else {
					axios({
						url: `${DESIGN_API_URL}/templates/${templateId}`,
						method: 'GET',
						cache: 'no-cache',
						signal: newAbortSignalFunc(timeoutAmount),
						cancelToken: templatesCancelToken.token,
					})
						.then((resp) => {
							clearTimeout(timeout);

							if (resp.data.status === 0) {
								resolve(unwrapResult(resp.data));
							} else {
								reject(resp.data);
							}
						})
						.catch((err) => {
							clearTimeout(timeout);

							reject(err);
						});
				}
			});
		},

		update: (templateId, templateData) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				const payload = toDotNotationPatchPayload(
					sanitizeTemplateMutationPayload(templateData),
				);

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				// Handle both single ID and array of IDs
				const isBulk = Array.isArray(templateId);
				const url = isBulk
					? `${DESIGN_API_URL}/templates/:keys?:keys=${encodeURIComponent(JSON.stringify(templateId))}`
					: `${DESIGN_API_URL}/templates/${templateId}`;

				axios({
					url: url,
					method: 'PATCH',
					cache: 'no-cache',
					data: payload,
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(unwrapResult(resp.data));
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		// Sync a listing-overlay template's default preview/caption to every TemplateSetting row that
		// uses it. Called from the template save flow after a successful update of an overlay template.
		syncListingOverlayDefault: (payload) => {
			const bayToken = Cookies.get('bayToken');

			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				bayAxios({
					url: `${BAY_API_URL}/listing-overlay/sync-listing-overlay-default`,
					method: 'POST',
					cache: 'no-cache',
					data: payload,
					headers: bayToken ? { 'X-Session-Token': bayToken } : {},
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		// Repoint every TemplateSetting row using old_template_id to template_id (Stop Using /
		// Deactivate / Delete of a listing overlay). Admins only rewrite their own rows server-side.
		replaceListingOverlayTemplate: (payload) => {
			const bayToken = Cookies.get('bayToken');

			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				bayAxios({
					url: `${BAY_API_URL}/listing-overlay/replace-template`,
					method: 'POST',
					cache: 'no-cache',
					data: payload,
					headers: bayToken ? { 'X-Session-Token': bayToken } : {},
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		delete: (templateId) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				// Handle both single ID and array of IDs
				const isBulk = Array.isArray(templateId);
				const url = isBulk
					? `${DESIGN_API_URL}/templates/:keys?:keys=${encodeURIComponent(JSON.stringify(templateId))}`
					: `${DESIGN_API_URL}/templates/${templateId}`;

				axios({
					url: url,
					method: 'DELETE',
					cache: 'no-cache',
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						if (resp.data.status === 0) {
							resolve(unwrapResult(resp.data));
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		archive: (templateId) => {
			return this.update(templateId, { status: 'archived' });
		},

		hideAllBayTemplates: (hideNonproprietaryTemplates) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/customers/CURRENT/prefs`,
					method: 'PATCH',
					cache: 'no-cache',
					data: {
						hideNonproprietaryTemplates: hideNonproprietaryTemplates,
					},
					signal: controller.signal,
					cancelToken: templatesCancelToken.token,
				})
					.then((resp) => {
						clearTimeout(timeout);
						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},

		hideAllBayEmailTemplates: (hideEmailTemplates) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				axios({
					url: `${DESIGN_API_URL}/customers/CURRENT/prefs`,
					method: 'PATCH',
					cache: 'no-cache',
					data: {
						hideEmailTemplates: hideEmailTemplates,
					},
					signal: controller.signal,
					cancelToken: templatesCancelToken.token,
				})
					.then((resp) => {
						clearTimeout(timeout);
						resolve(resp.data);
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},

		setVisibility: (templateId, visibility, userKey) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				if (!userKey) {
					clearTimeout(timeout);
					reject('User key is required');
					return;
				}

				axios({
					url: `${DESIGN_API_URL}/templates/${templateId}`,
					method: 'PATCH',
					data: {
						[`prefsOf.customers.${userKey}.hide`]: visibility,
					},
					cache: 'no-cache',
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);

						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		uiSupport: (ownerId = null) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				const hasOwnerParam =
					ownerId !== undefined && ownerId !== null && ownerId !== '';

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				// template / templateKey / prefs
				axios({
					url: `${DESIGN_API_URL}/templates/NEW${hasOwnerParam ? `?owner=${ownerId}` : ''}`,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
					cancelToken: templatesCancelToken.token,
				})
					.then((resp) => {
						clearTimeout(timeout);

						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		isIdSearch: (value) => isHashSearchValue(value),

		getQueryParams: (user, params, isSuperRole) => {
			const rawSearch =
				typeof params?.search === 'string' ? params.search.trim() : '';

			// A 24-char id identifies exactly one template, so it is looked up on its
			// own. Every other clause — owner, status, variant, and the email/overlay/
			// media exclusions the callers append — ANDs against that id and hides the
			// very record the admin asked for: an overlay searched from the Design tab,
			// an email template, anything whose status is outside the active filter.
			// Callers check `isIdSearch` and skip their own clauses for the same reason.
			if (isHashSearchValue(rawSearch)) {
				return { _id: rawSearch };
			}

			let ownerArray =
				params?.owner && params?.owner !== ''
					? [params?.owner && params?.owner === '0' ? 0 : params?.owner]
					: [user?.customer?._id, 0];

			let getStatusArray = (idNumber, ownerArrayItem, status) => {
				let constructedQuery = '';

				if (ownerArrayItem !== 0) {
					constructedQuery =
						status === '' ? { $in: ['active', 'draft'] } : status;
				} else {
					if (status === '') {
						// "draft", "inactive", "review", "archived"
						constructedQuery =
							idNumber === 0
								? { $in: ['active', 'draft', 'inactive'] }
								: 'active';
					} else {
						constructedQuery = idNumber === 0 ? status : 'active';
					}
				}

				return constructedQuery;
			};

			let sp = {
				search: rawSearch
					? rawSearch
							.split(/\s+/)
							.map((e) => escapeRegExp(e))
							.join('.*')
					: '',
				status: params?.status || '',
			};

			let queryParams = {
				variant: 'design',
			};

			if (!isSuperRole) {
				// Non-super (admin) roles see two owners at once by default: their own
				// company AND Back At You (ownerArray = [customerId, 0]). Each owner has
				// different status rules — a company sees its own drafts, but BAY is
				// restricted to 'active' only (see getStatusArray). That per-owner scoping
				// lives entirely in the $or branch below.
				//
				// This condition decides when we can SKIP the $or and collapse to a single
				// flat status filter. That is only safe when a single specific owner is in
				// play (ownerArray.length === 1, i.e. the user explicitly picked one owner
				// in the filter). It must be AND, not OR:
				//
				//   - AND: collapse only when a specific owner is selected. A status filter
				//     with NO owner selected (length 2) falls through to the $or, which
				//     still forces BAY -> 'active'. Correct.
				//   - OR (the bug): any status filter collapses to a flat `status`, dropping
				//     the owner split entirely — so the query has no owner clause and the
				//     backend returns that status for ALL owners, leaking BAY drafts to
				//     admins. (Regression from commit 60a905a7.)
				//
				// The owner-selected cases are safe either way because the UI disables the
				// status select to 'active' whenever an admin picks BAY as the owner.
				if (ownerArray.length === 1 && sp.status !== '') {
					queryParams = {
						...queryParams,
						status: getStatusArray(
							user?.customer?.idNumber,
							ownerArray[0],
							sp.status,
						),
					};
				} else {
					// ELSE get all templates for the owner templates and BAY templates
					queryParams['$or'] = [];

					for (const ownerArrayItem of ownerArray) {
						const isBay = ownerArrayItem === 0;
						const isBayUser = user?.customer?.idNumber === 0;

						// An admin only ever sees BAY's 'active' records (getStatusArray),
						// so a status filter that is not 'active' cannot match any of
						// them. Emitting the branch anyway returned every active BAY
						// record under a "Draft" filter (monday 13290840068).
						if (
							isBay &&
							!isBayUser &&
							sp.status !== '' &&
							sp.status !== 'active'
						) {
							continue;
						}

						// Guard against an ownerless (match-EVERYTHING) branch. The non-BAY
						// owner id comes from user.customer._id, which is empty on the first
						// read (actingAsState is a post-mount clone of userState and isn't
						// hydrated yet). An empty id serializes the owner clause to `{}`,
						// which in an $or matches ALL owners and leaks BAY drafts into an
						// admin's results. If the id is missing, skip that branch entirely —
						// the query then returns at most the BAY -> 'active' branch, never a
						// draft, and self-corrects on the next read once the id is present.
						if (
							!isBay &&
							(ownerArrayItem === undefined ||
								ownerArrayItem === null ||
								ownerArrayItem === '')
						) {
							continue;
						}

						queryParams['$or'].push({
							$and: [
								{
									[isBay ? 'owner.idNumber' : 'owner._id']: ownerArrayItem,
								},
								{
									status: getStatusArray(
										user?.customer?.idNumber,
										ownerArrayItem,
										sp.status,
									),
								},
							],
						});
					}

					// Both branches can be skipped (missing company id + a non-active
					// status filter). Mongo rejects an empty $or, so match nothing instead.
					if (queryParams['$or'].length === 0) {
						queryParams['$or'].push({ _id: { $in: [] } });
					}
				}
			} else if (isSuperRole) {
				// If super role, get all templates for all owners
				queryParams = {
					...queryParams,
					status:
						sp.status === ''
							? { $in: ['active', 'draft', 'inactive', 'review', 'archived'] }
							: sp.status,
				};
			}

			// An id search already returned above, so anything reaching here is a name.
			if (!isNil(sp.search) && sp.search !== '') {
				queryParams.name = {
					$regex: `${sp.search}`,
					$options: 'i',
				};
			}

			if (!isNil(sp.sortBy) && !isNil(sp.sortDir)) {
				queryParams[sp.sortBy] = sp.sortDir;
			}

			if (!isNil(sp.limit)) {
				queryParams.limit = sp.limit;
			}

			if (!isNil(sp.offset)) {
				queryParams.offset = sp.offset;
			}

			// The API filters on the resolved `isHidden`, but only as a boolean: `{ $ne: true }` is not honoured.
			if (params?.templateDisplay === 'hidden') {
				queryParams.isHidden = true;
			} else if (params?.templateDisplay === 'shown') {
				queryParams.isHidden = false;
			}

			return queryParams;
		},
		// MARK: Read category templates
		readCategoryTemplates: (categoryName, customerId) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();

				let timeout = setTimeout(() => {
					controller.abort();
				}, 180000);

				// NOTE: ["0::${categoryName}"] is a another way to get the category templates
				//"owner": "0" customerId,
				axios({
					url: `${DESIGN_API_URL}/templates?havingAllOfTags$Category=["${customerId}::${categoryName}"]`,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
					/* params: {
            havingAllOfTags$Category: JSON.stringify({
              "name": categoryName,
            }),
          } */
				})
					.then((resp) => {
						clearTimeout(timeout);

						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);

						reject(err);
					});
			});
		},

		// MARK: BULK OPERATIONS
		// Job-based bulk operations with progress monitoring capabilities

		bulkUpdate: (templateIds, updateData) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				let timeout = setTimeout(() => controller.abort(), 180000);

				// Use job-based pattern for large operations
				const runInForeground = templateIds.length <= 10; // Small ops run immediately

				axios({
					url: `${DESIGN_API_URL}/jobs.template.update`,
					method: 'POST',
					data: {
						src: { keys: templateIds },
						updateData,
						runInForeground,
					},
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);
						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},

		bulkDelete: (templateIds) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				let timeout = setTimeout(() => controller.abort(), 180000);

				// Use job-based pattern like entries.delete
				const runInForeground = templateIds.length <= 10;

				axios({
					url: `${DESIGN_API_URL}/jobs.template.deletion`,
					method: 'POST',
					data: {
						src: { keys: templateIds },
						runInForeground,
					},
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);
						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},

		bulkDuplicate: (templateIds) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				let timeout = setTimeout(() => controller.abort(), 180000);

				// Use job-based pattern like entries.move with isCopy=true
				const runInForeground = templateIds.length <= 10;

				axios({
					url: `${DESIGN_API_URL}/jobs.template.duplication`,
					method: 'POST',
					data: {
						src: { keys: templateIds },
						runInForeground,
					},
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);
						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},

		bulkSetVisibility: (templateIds, visibility, userKey) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				let timeout = setTimeout(() => controller.abort(), 180000);

				if (!userKey) {
					reject('User key is required');
					return;
				}

				// Use job-based pattern for visibility updates
				const runInForeground = templateIds.length <= 10;

				axios({
					url: `${DESIGN_API_URL}/jobs.template.visibility`,
					method: 'POST',
					data: {
						src: { keys: templateIds },
						updateData: { [`prefsOf.customers.${userKey}.hide`]: visibility },
						runInForeground,
					},
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);
						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},

		// Progress monitoring for bulk jobs
		getBulkJobProgress: (jobId) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				let timeout = setTimeout(() => controller.abort(), 30000); // Shorter timeout for progress checks

				axios({
					url: `${DESIGN_API_URL}/jobs/${jobId}/progress`,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);
						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},

		// Get job status and results
		getBulkJobStatus: (jobId) => {
			return new Promise((resolve, reject) => {
				const controller = new AbortController();
				let timeout = setTimeout(() => controller.abort(), 30000);

				axios({
					url: `${DESIGN_API_URL}/jobs/${jobId}`,
					method: 'GET',
					cache: 'no-cache',
					signal: controller.signal,
				})
					.then((resp) => {
						clearTimeout(timeout);
						if (resp.data.status === 0) {
							resolve(resp.data);
						} else {
							reject(resp.data);
						}
					})
					.catch((err) => {
						clearTimeout(timeout);
						reject(err);
					});
			});
		},
	};
};

const singleton = templates();
Object.freeze(singleton);

export default singleton;
